import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { statusesAPI, usersAPI } from '../services/api';

export default function Settings() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#2196F3', value: '' });

  // Функция для извлечения текста ошибки
  const getErrorMessage = (err, defaultMsg = 'Ошибка') => {
    if (err.response?.data?.error) return err.response.data.error;
    if (err.response?.data?.message) return err.response.data.message;
    if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
      return err.response.data.errors.map(e => e.msg || e.message || e).join(', ');
    }
    return defaultMsg;
  };

  const projectStatuses = [
    { value: 'развитие с нуля', label: 'развитие с нуля', color: '#4CAF50' },
    { value: 'развитие покупают', label: 'развитие покупают', color: '#4CAF50' },
    { value: 'регулярный успешный', label: 'регулярный успешный', color: '#4CAF50' },
    { value: 'угасает', label: 'угасает', color: '#FF9800' },
    { value: 'закрыт', label: 'закрыт', color: '#F44336' }
  ];

  const clientStatuses = [
    { value: 'закупает регулярный', label: 'закупает регулярный', color: '#4CAF50' },
    { value: 'закупает редко', label: 'закупает редко', color: '#4CAF50' },
    { value: 'порулонщик', label: 'порулонщик', color: '#FF9800' },
    { value: 'отработан, у конкурентов', label: 'отработан, у конкурентов', color: '#FF9800' },
    { value: 'общаемся, но не покупает', label: 'общаемся, но не покупает', color: '#FF9800' },
    { value: 'не контактировали', label: 'не контактировали', color: '#F44336' },
    { value: 'отработан, не с рынка', label: 'отработан, не с рынка', color: '#F44336' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusesRes, usersRes] = await Promise.all([
        statusesAPI.getAll(),
        usersAPI.getAll()
      ]);
      setStatuses(statusesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    if (item) {
      setEditingItem(item);
      if (type === 'user') {
        setFormData({
          name: item.fullName || '',
          email: item.email || '',
          password: ''
        });
      } else {
        setFormData({
          name: item.name || item.label,
          color: item.color,
          value: item.value || ''
        });
      }
    } else {
      setEditingItem(null);
      if (type === 'user') {
        setFormData({ name: '', email: '', password: '' });
      } else {
        setFormData({ name: '', color: '#2196F3', value: '' });
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'taskStatus') {
        if (editingItem) {
          await statusesAPI.update(editingItem.id, formData);
        } else {
          await statusesAPI.create(formData);
        }
      } else if (dialogType === 'user') {
        if (editingItem) {
          await usersAPI.update(editingItem.id, formData);
        } else {
          await usersAPI.create(formData);
        }
      }
      await loadData();
      handleCloseDialog();
    } catch (err) {
      setError(getErrorMessage(err, 'Ошибка сохранения'));
    }
  };

  const handleDelete = async (type, id) => {
    if (window.confirm('Удалить?')) {
      try {
        if (type === 'taskStatus') {
          await statusesAPI.delete(id);
        } else if (type === 'user') {
          await usersAPI.delete(id);
        }
        await loadData();
      } catch (err) {
        setError(getErrorMessage(err, 'Ошибка удаления'));
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold" mb={2}>
        Настройки
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
        sx={{ mb: 2 }}
      >
        <Tab label="Статусы задач" />
        <Tab label="Статусы проектов" />
        <Tab label="Статусы клиентов" />
        <Tab label="Пользователи" />
      </Tabs>

      {/* Статусы задач */}
      {tab === 0 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Статусы задач</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('taskStatus')}
                size="small"
              >
                Добавить
              </Button>
            </Box>
            <List>
              {statuses.map((status, index) => (
                <Box key={status.id}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenDialog('taskStatus', status)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('taskStatus', status.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <Chip
                      label={status.name}
                      sx={{
                        bgcolor: status.color,
                        color: 'white',
                        mr: 2
                      }}
                    />
                    <ListItemText primary={status.name} />
                  </ListItem>
                  {index < statuses.length - 1 && <Divider />}
                </Box>
              ))}
              {statuses.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  Нет статусов
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Статусы проектов */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Статусы проектов</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Эти статусы заданы в коде и используются в проектах
            </Typography>
            <List>
              {projectStatuses.map((status, index) => (
                <Box key={status.value}>
                  <ListItem>
                    <Chip
                      label={status.label}
                      sx={{
                        bgcolor: status.color,
                        color: 'white',
                        mr: 2
                      }}
                    />
                    <ListItemText
                      primary={status.label}
                      secondary={`Значение: ${status.value}`}
                    />
                  </ListItem>
                  {index < projectStatuses.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Статусы клиентов */}
      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Статусы клиентов</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Эти статусы заданы в коде и используются для клиентов
            </Typography>
            <List>
              {clientStatuses.map((status, index) => (
                <Box key={status.value}>
                  <ListItem>
                    <Chip
                      label={status.label}
                      sx={{
                        bgcolor: status.color,
                        color: 'white',
                        mr: 2
                      }}
                    />
                    <ListItemText
                      primary={status.label}
                      secondary={`Значение: ${status.value}`}
                    />
                  </ListItem>
                  {index < clientStatuses.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Пользователи */}
      {tab === 3 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Пользователи</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('user')}
                size="small"
              >
                Добавить
              </Button>
            </Box>
            <List>
              {users.map((user, index) => (
                <Box key={user.id}>
                  <ListItem
                    secondaryAction={
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenDialog('user', user)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('user', user.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={user.fullName}
                      secondary={user.email}
                    />
                  </ListItem>
                  {index < users.length - 1 && <Divider />}
                </Box>
              ))}
              {users.length === 0 && (
                <Typography color="text.secondary" align="center" py={2}>
                  Нет пользователей
                </Typography>
              )}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingItem ? 'Редактировать' : 'Добавить'} {
            dialogType === 'taskStatus' ? 'статус' :
              dialogType === 'user' ? 'пользователя' : ''
          }
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={dialogType === 'user' ? 'Полное имя' : 'Название'}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              size="small"
            />
            {dialogType === 'taskStatus' && (
              <TextField
                label="Цвет"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                fullWidth
                size="small"
              />
            )}
            {dialogType === 'user' && (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  fullWidth
                  size="small"
                />
                {!editingItem && (
                  <TextField
                    label="Пароль"
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    fullWidth
                    size="small"
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              dialogType === 'user'
                ? (!formData.name || !formData.email || (!editingItem && !formData.password))
                : !formData.name
            }
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
